import { useState, useEffect, useRef, useCallback } from 'react';
import { openObsChannel } from './remoteTransport';
import { MSG, HEARTBEAT_MS } from './remoteProtocol';

/**
 * Console side of the livestream output (/obs). While a pairing code exists it
 * sends the on-air content (what the projector shows, minus the background) to
 * the OBS browser source, whenever it changes and every few seconds so the
 * page can tell the console is still there.
 */
export default function useObsOutput({ code, isLive, content, currentLine, slides, style }) {
  const [status, setStatus] = useState('offline');
  const remoteRef = useRef(null);
  const lastSentRef = useRef('');

  const output = {
    isLive: !!isLive,
    content,
    currentLine,
    slides,
    style: {
      fontColor: style.fontColor,
      fontFamily: style.fontFamily,
      fontSize: style.fontSize,
      textEffect: style.textEffect,
    },
  };
  const outputRef = useRef(output);
  outputRef.current = output;

  const send = useCallback((force) => {
    const remote = remoteRef.current;
    if (!remote) return;
    const json = JSON.stringify(outputRef.current);
    if (!force && json === lastSentRef.current) return;
    lastSentRef.current = json;
    remote.send({ t: MSG.OUTPUT, output: outputRef.current });
  }, []);

  useEffect(() => {
    if (!code) return undefined;
    lastSentRef.current = '';
    const remote = openObsChannel(code, {
      onStatus: setStatus,
      onMessage: (msg) => { if (msg.t === MSG.HELLO) send(true); },
    });
    remoteRef.current = remote;
    if (!remote) return undefined;
    const timer = setInterval(() => send(true), HEARTBEAT_MS);
    return () => {
      clearInterval(timer);
      remote.close();
      remoteRef.current = null;
      setStatus('offline');
    };
  }, [code, send]);

  const json = JSON.stringify(output);
  useEffect(() => { send(false); }, [json, send, status]);

  return { status };
}
